// === src/components/intrare/Edit.jsx ===
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Container,
  Alert,
  Button,
  ButtonGroup,
} from "react-bootstrap";
import { authFetch } from "../utils/authFetch";
import IntrareForm from "./Form";
import DocumentToolbar from "../common/DocumentToolbar";
import { confirmDocumentLeave } from "../common/DocumentToolbar";
import useDocumentClosing from "../../hooks/useDocumentClosing";
import ViewNotaModal from "../ViewNotaModal";
import HelpModal from "../common/HelpModal";
import TreeHelp from "../common/TreeHelp";
import { useHelpHotkey } from "../../hooks/useHelpHotkey";
import { useAuth } from "../auth/AuthContext";


const normalizeForUI = (data) => ({
  ...data,
  tip_document: [2, 8].includes(Number(data.tip_factura)) && (!data.tip_document || data.tip_document === "Factura")
    ? (Number(data.tip_factura) === 2 ? "Aviz" : "Borderou")
    : (data.tip_document || "Factura"),
  doc_no: data.doc_no ?? data.bxdoc_no ?? "",
  doc_date: data.doc_date ?? data.bxdoc_date ?? null,
  input_date: data.input_date ?? data.bxinput_date ?? null,
  datascadenta: data.datascadenta ?? data.bxdatascadenta ?? null,
  tip_factura: data.tip_factura ?? data.bxtip_factura ?? 1, // default daca vrei
  bxcont_code: data.bxcont_code,
  linii: (data.linii || []).map((ln) => ({
    ...ln,
    qty: ln.qty ?? ln.bxcantitate ?? "",
    price: ln.price ?? ln.bxvprice ?? "",
    amount: ln.amount ?? ln.bxvamount ?? "",
    vat_amount: ln.vat_amount ?? ln.bxvatv_amount ?? "",
    total_amount: ln.total_amount ?? ln.bxtotalv_amount ?? "",
  })),
});

function normalizeIntrare(m) {
  if (!m) return null;
  const {
    id,
    created_at,
    updated_at,
    user_id,
    contact_name, // ignora campuri nerelevante pt. "dirty"
    total,
    total_tva,
    total_fara_tva, // daca vin calculate de backend
    linii = [],
    ...rest
  } = m;

  const normLinii = [...linii]
    .map(({ id, created_at, updated_at, _tmp, ...ln }) => ln)
    .sort((a, b) => {
      const ka = a.crt ?? a.bxpart_id ?? 0;
      const kb = b.crt ?? b.bxpart_id ?? 0;
      return ka === kb ? 0 : ka < kb ? -1 : 1;
    });

  return { ...rest, linii: normLinii };
}
const hash = (obj) => JSON.stringify(obj);

export default function IntrareEdit() {
  const { user } = useAuth();
  const closingEnabled = useDocumentClosing();
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);

  const [form, setForm] = useState(null);
  const [initialForm, setInitialForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [existingNir, setExistingNir] = useState(null);
  const [showNota, setShowNota] = useState(false);
  const { showHelp, setShowHelp } = useHelpHotkey();
  const formRef = useRef(null);
  const allocationRef = useRef(null);
  const [supplierAcc, setSupplierAcc] = useState(null);
  // ✅ IMPORTANT: pastreaza cost_alloc daca se pierde dupa onChange (merge defensiv)

  const handleChange = useCallback((v) => {
    setForm((prev) => {
      const nextVal = typeof v === "function" ? v(prev) : v;
      if (!prev) return nextVal;

      const prevLines = Array.isArray(prev.linii) ? prev.linii : [];
      const nextLines = Array.isArray(nextVal?.linii) ? nextVal.linii : [];

      // indexam liniile vechi dupa id (si fallback dupa uid daca exista)
      const prevById = new Map();
      const prevByUid = new Map();

      for (const ln of prevLines) {
        if (ln?.id != null) prevById.set(Number(ln.id), ln);
        if (ln?.uid) prevByUid.set(String(ln.uid), ln);
      }

      const mergedLines = nextLines.map((ln) => {
        const keyId = ln?.id != null ? Number(ln.id) : null;
        const keyUid = ln?.uid ? String(ln.uid) : null;

        const old =
          (keyId != null && prevById.get(keyId)) ||
          (keyUid && prevByUid.get(keyUid)) ||
          null;

        // daca noul ln nu are cost_alloc dar vechiul avea, il pastram
        if (
          old &&
          ln?.cost_alloc == null && // undefined/null
          Array.isArray(old.cost_alloc) &&
          old.cost_alloc.length > 0
        ) {
          return { ...ln, cost_alloc: old.cost_alloc };
        }

        return ln;
      });

      return { ...nextVal, linii: mergedLines };
    });
  }, []);

  // incarcare intrare

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await authFetch(`/intrare/${id}`);
        const ui = normalizeForUI(data);
        //
        // normalize label for UI
        const contactLabel =
          ui.contact_label || ui.contact_name || ui.contactName || "";

        const normalized = {
          ...ui,
          contact_label: contactLabel,
        };

        setForm(normalized);
        setInitialForm(normalized);
      } catch (e) {
        setError(String(e.message || e));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const sumAlloc = (ln) =>
    (ln.cost_alloc || []).reduce((s, a) => s + (Number(a.amount) || 0), 0);

  const allocDiff = (ln) => (Number(ln.amount) || 0) - sumAlloc(ln);

  const isAllocOk = (ln, eps = 0.01) => Math.abs(allocDiff(ln)) <= eps; // 1 ban

  // afla daca exista NIR pentru aceasta intrare
  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`/nir/by-intrare/${id}`);
        console.log("NIR existing:1------", `/nir/by-intrare/${id}`);
        console.log("NIR existing:2------", res);
        setExistingNir(res && res.id ? res : null);
        console.log("exista NIR ------");
      } catch {
        // ignora
      }
    })();
  }, [id]);

  // detectare „dirty”
  const originalHash = useMemo(
    () => hash(normalizeIntrare(initialForm)),
    [initialForm],
  );
  const currentHash = useMemo(() => hash(normalizeIntrare(form)), [form]);
  const isDirty = originalHash !== currentHash;

  // stare document
  const isFinal = !!form?.bxfinal_bool;

  // salvare
  const save = async () => {
    setSaving(true);
    setError("");

    const payload = {
      ...form,
      linii: (form.linii || []).map((ln) => ({
        ...ln,
        cost_alloc: (Array.isArray(ln.cost_alloc) ? ln.cost_alloc : [])
          .map((a) => ({
            cost_center_id: Number(a.cost_center_id),
            amount: Number(a.amount) || 0,
            percent: a.percent != null ? Number(a.percent) : undefined,
          }))
          .filter((a) => a.cost_center_id && a.amount !== 0),
      })),
    };

    try {
      console.log(
        "SAVE payload line[2] cost_alloc:",
        form.linii?.[2]?.cost_alloc,
      );
      console.log("PUT intrare payload:", JSON.stringify(form, null, 2));

      console.log(
        "PUT payload lines:",
        payload.linii.map((ln) => ({
          id: ln.id ?? null,
          uid: ln.row_uid,
          descr: ln.bxdescr,
          cont: ln.bxcont_code,
          alloc_len: (ln.cost_alloc || []).length,
          alloc: ln.cost_alloc,
        })),
      );
      const saved = await authFetch(`/intrare/${id}`, {
        method: "PUT",
        headers: { "If-Match": initialForm?.revision },
        body: JSON.stringify(await allocationRef.current.prepare(payload)),
      });

      const ui = normalizeForUI(saved);
      setForm(ui);
      setInitialForm(ui);
      toast.success("Intrarea a fost salvata cu succes!");
    } catch (e) {
      setError(String(e.message || e));
      toast.error(`Eroare la salvare: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  const finalize = async () => {
    if (
      !window.confirm(
        "Sigur doresti sa finalizezi aceasta intrare? Dupa finalizare, intrarea si NIR-ul asociat nu mai pot fi editate!",
      )
    ) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      await authFetch(`/intrare/${id}/finalize`, { method: "POST" });
      toast.success("Intrarea a fost finalizata cu succes!");
      const data = await authFetch(`/intrare/${id}`);
      const ui = normalizeForUI(data);
      setForm(ui);
      setInitialForm(ui);

      // De asemenea, daca existNir e actualizat:
      const res = await authFetch(`/nir/by-intrare/${id}`);
      setExistingNir(res && res.id ? res : null);
    } catch (e) {
      setError(String(e.message || e));
      toast.error(`Eroare la finalizare: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  const unfinalize = async () => {
    if (
      !window.confirm(
        "Sigur doresti sa de-finalizezi aceasta intrare? Atat factura cat si NIR-ul asociat vor reveni in starea de ciorna si vor putea fi editate din nou.",
      )
    ) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      await authFetch(`/intrare/${id}/unfinalize`, { method: "POST" });
      toast.success("Documentul a fost redeschis cu succes!");
      const data = await authFetch(`/intrare/${id}`);
      const ui = normalizeForUI(data);
      setForm(ui);
      setInitialForm(ui);

      // De asemenea, daca existNir e actualizat:
      const res = await authFetch(`/nir/by-intrare/${id}`);
      setExistingNir(res && res.id ? res : null);
    } catch (e) {
      setError(String(e.message || e));
      toast.error(`Eroare la de-finalizare: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  // save „silent” (fara UI changes mari) pentru Genereaza NIR
  const saveSilent = async () => {
    const payload = {
      ...form,
      linii: (form.linii || []).map((ln) => ({
        ...ln,
        cost_alloc: (Array.isArray(ln.cost_alloc) ? ln.cost_alloc : [])
          .map((a) => ({
            cost_center_id: Number(a.cost_center_id),
            amount: Number(a.amount) || 0,
            percent: a.percent != null ? Number(a.percent) : undefined,
          }))
          .filter((a) => a.cost_center_id && a.amount !== 0),
      })),
    };
    const data = await authFetch(`/intrare/${id}`, {
      method: "PUT",
      headers: { "If-Match": initialForm?.revision },
      body: JSON.stringify(await allocationRef.current.prepare(payload)),
    });
    const ui = normalizeForUI(data);
    setForm(ui);
    setInitialForm(ui);
  };

  const handleGenerateNir = async () => {
    // 1) daca formularul are modificari nesalvate, salveaza-l intai
    const refDirty = formRef.current?.isDirty;
    if (refDirty || isDirty) {
      const ok = window.confirm(
        "Formularul are modificari. Salvez inainte de a genera NIR?",
      );
      if (!ok) return;
      try {
        if (formRef.current?.save) {
          await formRef.current.save(); // daca IntrareForm expune save()
        } else {
          await saveSilent(); // fallback
        }
      } catch {
        alert(
          "Nu am putut salva intrarea. Verifica erorile si incearca din nou.",
        );
        return;
      }
    }

    // 2) daca exista deja un NIR din aceasta intrare, ofera deschiderea lui
    if (existingNir) {
      const openExisting = window.confirm(
        "Exista deja un NIR generat din aceasta intrare. Deschid nota existenta?",
      );
      if (openExisting) {
        navigate(`/nir/${existingNir.id}/edit`);
        return;
      }
    }

    // 3) mergi in wizardul NIR (create) cu from_intrare
    navigate(`/nir/new?from_intrare=${id}`);
  };

  const handleUpdateNir = async () => {
    // 1) daca formularul are modificari nesalvate, salveaza-l intai
    const refDirty = formRef.current?.isDirty;
    if (refDirty || isDirty) {
      const ok = window.confirm(
        "Formularul are modificari. Salvez inainte de a actualiza NIR?",
      );
      if (!ok) return;
      try {
        if (formRef.current?.save) {
          await formRef.current.save();
        } else {
          await saveSilent();
        }
      } catch {
        alert(
          "Nu am putut salva intrarea. Verifica erorile si incearca din nou.",
        );
        return;
      }
    }

    // 2) mergi in wizardul NIR (create) cu from_intrare pentru actualizare
    navigate(`/nir/new?from_intrare=${id}`);
  };

  // avertizare la parasirea paginii daca e nesalvat

  if (loading) return <Container className="py-3">Se incarca…</Container>;
  if (!form) {
    return (
      <Container className="py-3 text-danger">
        Nu s-a gasit inregistrarea.
      </Container>
    );
  }

  return (
    <Container className="py-3">
      <DocumentToolbar
        title={`Intrare #${id}`}
        dirty={isDirty} error={error}
        isFinal={isFinal} busy={saving} closingEnabled={closingEnabled}
        onBack={() => form?.bxcont_id ? navigate(`/intrare/intrare_list/${form.bxcont_id}`) : navigate(-1)}
        addLabel="Adaugă intrare"
        onAdd={() => navigate(`/intrare/new?account_id=${form.bxcont_id || ""}&bxsoldint=${form.bxsoldint || 0}`)}
        onSave={save} saveDisabled={!isDirty} onFinalize={finalize} finalizeDisabled={isDirty}
        canReopen={user?.is_superuser || user?.user_name === "admin" || user?.perms?.includes("INTRARE_VALIDATE")}
        onReopen={unfinalize}
        actions={[
          { label: "Vezi nota contabilă", onClick: () => setShowNota(true) },
          existingNir && { label: "Vezi NIR", leavesDocument: true, onClick: () => navigate(`/nir/${existingNir.id}/edit`) },
          { label: "Ajutor (F1)", onClick: () => setShowHelp(true) },
        ]}
      />

      {error && <Alert variant="danger">{error}</Alert>}

      <IntrareForm allocationRef={allocationRef} value={form} onChange={setForm} onSubmit={save} locked={isFinal} />

      <div className="d-flex justify-content-between align-items-center mt-3">
        <div />
        <ButtonGroup>
          {existingNir && existingNir.bxfinal_bool === 1 && (
            <Button
              as={Link}
              to={`/nir/${existingNir.id}/edit`}
              onClick={event => { if (!confirmDocumentLeave(isDirty)) event.preventDefault(); }}
              variant="success"
            >
              Vezi NIR (Finalizat)
            </Button>
          )}
          {existingNir && existingNir.bxfinal_bool !== 1 && (
            <Button
              onClick={handleUpdateNir}
              variant="outline-success"
              disabled={isFinal}
            >
              Actualizeaza NIR
            </Button>
          )}
          {!existingNir && (
            <Button
              onClick={handleGenerateNir}
              variant="success"
              disabled={isFinal}
            >
              Genereaza NIR
            </Button>
          )}
        </ButtonGroup>
      </div>

      <HelpModal
        show={showHelp}
        onHide={() => setShowHelp(false)}
        title="Manual de Utilizare ERP"
      >
        <TreeHelp defaultChapter="intrari" />
      </HelpModal>


      <ViewNotaModal
        recType={0}
        docId={Number(id)}
        show={showNota}
        onHide={() => setShowNota(false)}
      />
    </Container>
  );
}
